import { useEffect, useState } from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import AnalyticsFilterItems from "./AnalyticsFilterItems";

interface AnalyticsFilterCardProps {
  data: LeadsManagementData;
}

export default function AnalyticsFilterCard({ data }: AnalyticsFilterCardProps) {
  const { refetch, isLoading, brand, start, end, setBrand, setStart, setEnd } = data;

  // Brand filters immediately on selection (setBrand is the real request
  // param, so it triggers its own fetch right away — same as before the
  // Get Data button existed). Start/end date only update this local draft;
  // the real start/end (and the fetch they trigger) only change once Get
  // Data is clicked. draftStart/draftEnd are synced back from the committed
  // values via the effects below so external changes still show up here.
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);

  useEffect(() => setDraftStart(start), [start]);
  useEffect(() => setDraftEnd(end), [end]);

  function handleGetData() {
    setStart(draftStart);
    setEnd(draftEnd);
  }

  return (
    <Box
      sx={{
        p: "0px 0px",
        borderRadius: 2,
        display: "flex",
        flexWrap: "wrap",
        gap: 2.25,
        alignItems: "flex-end",
      }}
    >
      <AnalyticsFilterItems
        data={data}
        draftBrand={brand}
        draftStart={draftStart}
        draftEnd={draftEnd}
        onBrandChange={setBrand}
        onStartChange={setDraftStart}
        onEndChange={setDraftEnd}
      />

      <Button
        variant="contained"
        onClick={handleGetData}
        disabled={isLoading}
        size="large"
        sx={{
          bgcolor: "secondary.main",
          "&:hover": { bgcolor: "secondary.dark" },
          borderRadius: 1,
          fontWeight: 500,
          letterSpacing: "0.4px",
          px: 3,
        }}
      >
        Get Data
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 0.5, ml: "auto" }}>
        <Tooltip title="Refresh">
          <IconButton onClick={refetch} disabled={isLoading} sx={{ color: "secondary.main" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
